import Sprite from '../ui/Sprite.js';
import Nature from './Nature.js';
import Status from './Status.js';
import Item from './Item.js';
import { imageStore, spriteStore } from '../images.js';
import { audioManager } from '../AudioManager.js';
import { moveSetManager } from '../data/managers/MoveSetManager.js';
import Move from '../entities/Move.js';
import { globalPokemonDataStore } from '../OverworldAnimation.js';
import {
    calculateEntryHazardDamage,
    calculateEntryHazardStatusEffect,
    calculateEntryHazardStatModification,
} from './EntryHazard.js';
import {
    pokedexClassificationsDB,
    pokedexEntriesDB,
    pokemonEvolutions,
    evDB,
    pokemonDB,
    typeDB,
    genderDB,
    catchRateDB,
} from '../data/pokemonDB.js';
import Pokeball from './Pokeball.js';
import ItemFactory from './ItemFactory.js';
import {
    pinchBerryStat,
    evVitaminStat,
    evReducingBerryStat,
    evVitaminItemIds,
    evReducingBerryItemIds,
    EV_TOTAL_MAX,
    xItemStat,
    xItemIds,
    EV_VITAMIN_STAT_CAP,
} from '../constants.js';

/** Sentinel for "ability suppressed" (e.g. Gastro Acid). When set as abilityOverride, effective ability is null. */
export const ABILITY_SUPPRESSED = Symbol('ability_suppressed');

export default class Pokemon {
    serialize() {
        console.log(`Serializing ${this.constructor.name}`);
        return {
            // Constructor parameters
            id: this.id,
            level: this.level,
            battleStages: this.battleStages,
            ev: this.ev,
            iv: this.iv,
            experienceType: this.experienceType,

            // Store trainer IDs instead of full objects
            originalTrainerId: this.originalTrainer?.id || null,
            currentTrainerId: this.currentTrainer?.id || null,

            // Moves - just plain objects
            moves: {
                0: this.moves[0] || null,
                1: this.moves[1] || null,
                2: this.moves[2] || null,
                3: this.moves[3] || null,
            },

            // State that changes after construction
            nickname: this.nickname,
            heldItem: this.heldItem?.id ?? null,
            experience: this.experience,
            stats: this.stats,
            canEvolve: this.canEvolve,
            status1: this.status1 ? { id: this.status1.id, startTurn: this.status1.startTurn } : null,
            status2: this.status2 ? { id: this.status2.id, startTurn: this.status2.startTurn } : null,
            statIncreaseHistory: this.statIncreaseHistory,
            moveHistory: this.moveHistory || [],
            newMoves: this.newMoves || [],
            gender: this.gender,
            nature: this.nature,
            pokeball: this.pokeball ? this.pokeball.id : 'pokeball',
            // Persist base ability only; transient override is battle-only and not saved
            ability:
                this.baseAbility !== null && this.baseAbility !== undefined
                    ? {
                          identifier: this.baseAbility.identifier,
                          name: this.baseAbility.name,
                          description: this.baseAbility.description ?? null,
                      }
                    : null,
        };
    }

    static async deserialize(data) {
        // Create Pokemon with only the essential deserialized data
        // Everything else will be recalculated as if spawning a new Pokemon
        const pokemon = new Pokemon({
            id: data.id,
            level: data.level,
            battleStages: {
                attack: 0,
                defense: 0,
                specialAttack: 0,
                specialDefense: 0,
                speed: 0,
                accuracy: 0,
                evasiveness: 0,
            },
            ev: data.ev,
            iv: data.iv,
            moves: {
                0: Move.deserialize(data.moves[0]) || null,
                1: Move.deserialize(data.moves[1]) || null,
                2: Move.deserialize(data.moves[2]) || null,
                3: Move.deserialize(data.moves[3]) || null,
            },
            experienceType: data.experienceType,
            originalTrainer: {}, // Temporary empty object
        });

        // Restore only the specified deserialized properties
        pokemon.nickname = data.nickname;
        if (data.heldItem !== null && data.heldItem !== undefined) {
            try {
                pokemon.heldItem = ItemFactory.create(data.heldItem);
            } catch (_) {
                pokemon.heldItem = null;
            }
        } else {
            pokemon.heldItem = null;
        }
        pokemon.experience = data.experience;
        pokemon.experienceType = data.experienceType;
        pokemon.status1 = data.status1
            ? new Status({ id: data.status1.id, startTurn: data.status1.startTurn, pokemon })
            : null;
        pokemon.status2 = data.status2
            ? new Status({ id: data.status2.id, startTurn: data.status2.startTurn, pokemon })
            : null;
        pokemon.moveHistory = data.moveHistory || [];
        pokemon.gender = data.gender;
        pokemon.nature = new Nature(data.nature);

        // Restore base ability from save (constructor randomizes it otherwise)
        if (data.ability !== null && data.ability !== undefined) {
            pokemon.baseAbility = {
                identifier: data.ability.identifier,
                name: data.ability.name,
                description: data.ability.description ?? null,
            };
        }
        pokemon.abilityOverride = null;

        // Recalculate stats based on deserialized level, ev, iv, and deserialized nature
        pokemon.stats = pokemon.calculateStats(pokemon.id, {
            ev: pokemon.ev,
            iv: pokemon.iv,
            nature: pokemon.nature,
            level: pokemon.level,
        });

        // Restore current HP from save (calculateStats sets hp = maxHp)
        if (data.stats !== null && data.stats !== undefined && typeof data.stats.hp === 'number') {
            pokemon.stats.hp = Math.min(Math.max(0, data.stats.hp), pokemon.stats.maxHp);
        }

        // Recalculate canEvolve based on level
        pokemon.canEvolve = pokemon.checkCanEvolveViaLevelUp();

        // Recalculate statIncreaseHistory based on current level stats
        pokemon.statIncreaseHistory = {
            [pokemon.level]: {
                increase: {
                    hp: 0,
                    attack: 0,
                    defense: 0,
                    specialAttack: 0,
                    specialDefense: 0,
                    speed: 0,
                },
                total: {
                    hp: pokemon.stats.maxHp,
                    attack: pokemon.stats.attack,
                    defense: pokemon.stats.defense,
                    specialAttack: pokemon.stats.specialAttack,
                    specialDefense: pokemon.stats.specialDefense,
                    speed: pokemon.stats.speed,
                },
            },
        };

        // Reset newMoves to empty array
        pokemon.newMoves = [];

        // Store trainer IDs for later resolution
        pokemon._originalTrainerId = data.originalTrainerId;
        pokemon._currentTrainerId = data.currentTrainerId;
        if (!(data.pokeball instanceof String)) {
            pokemon.pokeball = ItemFactory.create('pokeball');
        } else {
            pokemon.pokeball = ItemFactory.create(data.pokeball);
        }

        return pokemon;
    }

    /**
     * Effective ability: override (e.g. Worry Seed → Insomnia), or suppressed (null), else base ability.
     * Override is cleared on switch out and battle end via resetVolatileStatuses().
     */
    get ability() {
        if (this.abilityOverride === ABILITY_SUPPRESSED) return null;
        return this.abilityOverride ?? this.baseAbility ?? null;
    }

    /** Set a temporary ability override (e.g. Insomnia from Worry Seed). Cleared on switch out / battle end. */
    setAbilityOverride(override) {
        this.abilityOverride = override;
    }

    /** Clear temporary ability override. Called from resetVolatileStatuses(). */
    clearAbilityOverride() {
        this.abilityOverride = null;
    }

    static async create(options) {
        const pokemon = new Pokemon(options);

        // TODO: Clean this up. We probably don't need these sprite promises.

        // Wait for all sprite resources to load
        const spritePromises = [
            // Front sprite
            new Promise((resolve) => {
                pokemon.handleAsyncResource(
                    imageStore.get('img/spritesheets/pokemon_front_master_spritesheet_20_20.png'),
                    (image) => {
                        pokemon.rawPokemonData.frontImage = image;
                        pokemon.frontSprite.setImage(image);
                        resolve();
                    },
                    'front sprite sheet'
                );
            }),

            // Back sprite
            new Promise((resolve) => {
                pokemon.handleAsyncResource(
                    imageStore.get('img/spritesheets/pokemon_back_master_spritesheet_20_20.png'),
                    (image) => {
                        pokemon.backSprite.setImage(image);
                        resolve();
                    },
                    'back sprite sheet'
                );
            }),

            // Mini sprite (icon)
            new Promise((resolve) => {
                pokemon.handleAsyncResource(
                    imageStore.get('img/spritesheets/pokemon_mini_master_spritesheet_20_20.png'),
                    (image) => {
                        pokemon.icon.setImage(image);
                        resolve();
                    },
                    'icon sprite'
                );
            }),

            // Stat increase animation
            new Promise((resolve) => {
                pokemon.handleAsyncResource(
                    imageStore.get('img/stat_boost_animation_v2.png'),
                    (image) => {
                        pokemon.statIncreaseAnimation.setImage(image);
                        resolve();
                    },
                    'stat increase animation'
                );
            }),

            // Stat decrease animation
            new Promise((resolve) => {
                pokemon.handleAsyncResource(
                    imageStore.get('img/stat_decrease_animation_v2.png'),
                    (image) => {
                        pokemon.statDecreaseAnimation.setImage(image);
                        resolve();
                    },
                    'stat decrease animation'
                );
            }),
        ];

        try {
            // Wait for all sprite resources to load
            await Promise.all(spritePromises);

            return pokemon;
        } catch (error) {
            console.error('Failed to create Pokemon:', error);
            throw error;
        }
    }

    async handleAsyncResource(resource, onSuccess, errorContext) {
        try {
            if (resource instanceof Promise) {
                const result = await resource;
                onSuccess(result);
            } else {
                onSuccess(resource);
            }
        } catch (error) {
            console.error(`Failed to load ${errorContext} for ${this.name}:`, error);
        }
    }

    createSprite(options = {}) {
        return new Sprite({
            position: { x: 0, y: 0 },
            image: null,
            ...options,
        });
    }

    static getRawPokemonDataV2(id) {
        // Ensure id is a number for consistent access
        const numericId = typeof id === 'string' ? parseInt(id, 10) : id;

        if (isNaN(numericId) || numericId === undefined || numericId === null) {
            console.error(`Invalid Pokemon ID: ${id} (type: ${typeof id})`);
            throw new Error(`Invalid Pokemon ID: ${id}. ID must be a number.`);
        }

        // Try both numeric and string keys since data might be stored either way
        const data = globalPokemonDataStore[numericId] || globalPokemonDataStore[String(numericId)];
        if (!data || !data.name) {
            console.error(
                `Pokemon data not found for ID: ${numericId}. Available IDs:`,
                Object.keys(globalPokemonDataStore).slice(0, 10)
            );
            throw new Error(
                `Pokemon data not loaded for ID: ${numericId}. Make sure Pokemon data is loaded before creating Pokemon instances.`
            );
        }
        return data;
    }

    static getRawPokemonData(id) {
        const rawPokemonData = pokemonDB[id - 1];
        rawPokemonData.evYield = evDB[id].evYield;
        rawPokemonData.baseStats = evDB[id].baseStats;
        rawPokemonData.type1 = typeDB[id].type1;
        rawPokemonData.type2 = typeDB[id].type2;
        rawPokemonData.genderDistribution =
            genderDB[id] !== undefined ? genderDB[id] : { male: 0.5, female: 0.5 };
        rawPokemonData.classification = pokedexClassificationsDB[id];
        rawPokemonData.pokedexEntry = pokedexEntriesDB[id];
        rawPokemonData.evolution = pokemonEvolutions[id].evolution;
        try {
            const rawMoveSetData = moveSetManager.apiLearnSets[id];
            rawPokemonData.moveSetByLevelUp = rawMoveSetData.moveSetByLevelUp ?? {};
            rawPokemonData.moveSetByEgg = rawMoveSetData.moveSetByEgg ?? {};
            rawPokemonData.moveSetByTutor = rawMoveSetData.moveSetByTutor ?? {};
            rawPokemonData.moveSetByHM = rawMoveSetData.moveSetByHM ?? {};
            rawPokemonData.moveSetByTM = rawMoveSetData.moveSetByTM ?? {};
        } catch (error) {
            console.log(moveSetManager);
            console.log(id);
            console.error(error);
        }
        return rawPokemonData;
    }

    getBaseStats() {
        const baseStats = {};
        // const rawPokemonData = Pokemon.getRawPokemonData(id);
        baseStats.maxHp = this.rawPokemonData.baseStats.maxHp;
        baseStats.attack = this.rawPokemonData.baseStats.attack;
        baseStats.defense = this.rawPokemonData.baseStats.defense;
        baseStats.specialAttack = this.rawPokemonData.baseStats.specialAttack;
        baseStats.specialDefense = this.rawPokemonData.baseStats.specialDefense;
        baseStats.speed = this.rawPokemonData.baseStats.speed;
        return baseStats;
    }

    canLearnNewMove() {
        return this.getMoveSet()[this.level].length > 0;
    }

    getMoveSet() {
        return globalPokemonDataStore[this.id].moveSetByLevelUp;
    }

    getMoves(moves) {
        if (moves) {
            // Check if moves has the correct format:
            // Should be an object with numeric keys (0-3) and string values
            const isValidFormat =
                typeof moves === 'object' &&
                moves !== null &&
                !Array.isArray(moves) &&
                Object.keys(moves).length > 0 &&
                Object.keys(moves).length <= 4 &&
                Object.keys(moves).every((key) => {
                    const numKey = parseInt(key);
                    return numKey >= 0 && numKey <= 3 && typeof moves[key] === 'string';
                });

            if (isValidFormat) {
                // Format is correct, use the provided moves
                const formattedMoves = {};
                formattedMoves[0] = moves[0] ? Move.getById(moves[0]) : null;
                formattedMoves[1] = moves[1] ? Move.getById(moves[1]) : null;
                formattedMoves[2] = moves[2] ? Move.getById(moves[2]) : null;
                formattedMoves[3] = moves[3] ? Move.getById(moves[3]) : null;
                return formattedMoves;
            }
            // If format is incorrect, proceed with the random generation logic below
        }

        // Original logic for generating random moves
        let possibleMoves = [];
        for (let i = 1; i <= this.level; i++) {
            const movesForCurrentLevel = this.moveSet[i];
            if (movesForCurrentLevel === undefined || movesForCurrentLevel === null) {
                continue;
            }
            possibleMoves = possibleMoves.concat(movesForCurrentLevel);
        }

        // Get random subset of moves
        const maxMoves = 4;
        const randomMoves = possibleMoves
            .sort(() => Math.random() - 0.5)
            .slice(0, Math.min(maxMoves, possibleMoves.length));

        const finalMoves = {};
        finalMoves[0] = randomMoves[0] ? Move.getById(randomMoves[0]) : null;
        finalMoves[1] = randomMoves[1] ? Move.getById(randomMoves[1]) : null;
        finalMoves[2] = randomMoves[2] ? Move.getById(randomMoves[2]) : null;
        finalMoves[3] = randomMoves[3] ? Move.getById(randomMoves[3]) : null;

        return finalMoves;
    }

    calculateStats(id, options) {
        const baseStats = globalPokemonDataStore[id].baseStats;
        const ev = options.ev;
        const iv = options.iv;
        const nature = options.nature;
        const level = options.level;
        const stats = {};
        stats.attack = Math.floor(
            Math.floor((2 * baseStats.attack + iv.attack + Math.floor(ev.attack / 4)) * (level / 100) + 5) *
                nature.statModifier('attack')
        );

        stats.defense = Math.floor(
            Math.floor(
                (2 * baseStats.defense + iv.defense + Math.floor(ev.defense / 4)) * (level / 100) + 5
            ) * nature.statModifier('defense')
        );

        stats.specialAttack = Math.floor(
            Math.floor(
                (2 * baseStats.specialAttack + iv.specialAttack + Math.floor(ev.specialAttack / 4)) *
                    (level / 100) +
                    5
            ) * nature.statModifier('specialAttack')
        );

        stats.specialDefense = Math.floor(
            Math.floor(
                (2 * baseStats.specialDefense + iv.specialDefense + Math.floor(ev.specialDefense / 4)) *
                    (level / 100) +
                    5
            ) * nature.statModifier('specialDefense')
        );

        stats.speed = Math.floor(
            Math.floor((2 * baseStats.speed + iv.speed + Math.floor(ev.speed / 4)) * (level / 100) + 5) *
                nature.statModifier('speed')
        );

        stats.maxHp = Math.floor(
            (iv.hp + 2 * baseStats.maxHp + Math.floor(ev.hp / 4)) * (level / 100) + 10 + level
        );
        const previousHp = options.previousHp;
        const previousMaxHp = options.previousMaxHp;
        if (typeof previousHp === 'number' && typeof previousMaxHp === 'number' && previousMaxHp > 0) {
            const ratio = previousHp / previousMaxHp;
            stats.hp = Math.min(stats.maxHp, Math.max(0, Math.floor(stats.maxHp * ratio)));
        } else {
            stats.hp = stats.maxHp;
        }
        return stats;
    }

    constructor({
        id,
        level,
        battleStages = {
            attack: 0,
            defense: 0,
            specialAttack: 0,
            specialDefense: 0,
            speed: 0,
            accuracy: 0,
            evasiveness: 0,
        },
        ev = {
            hp: 0,
            attack: 0,
            defense: 0,
            specialAttack: 0,
            specialDefense: 0,
            speed: 0,
        },
        iv = {
            hp: Math.floor(Math.random() * 32),
            attack: Math.floor(Math.random() * 32),
            defense: Math.floor(Math.random() * 32),
            specialAttack: Math.floor(Math.random() * 32),
            specialDefense: Math.floor(Math.random() * 32),
            speed: Math.floor(Math.random() * 32),
        },
        moves,
        experienceType = 'medium_fast',
        originalTrainer = {
            name: null,
            id: null,
        },
        pokeball = null,
    }) {
        // Basic Pokemon Data
        this.id = id;
        this.rawPokemonData = Pokemon.getRawPokemonDataV2(this.id);
        if (level !== null && level !== undefined) {
            this.level = level;
        } else {
            const maxLevel = this.rawPokemonData.evolution?.level ?? 100;
            this.level = Math.max(12, Math.floor(Math.random() * maxLevel));
        }
        this.name = this.rawPokemonData.name;
        this.nickname = this.name;
        this.originalTrainer = {
            name: originalTrainer.name,
            id: originalTrainer.id,
        };
        this.currentTrainer = {
            name: originalTrainer.name,
            id: originalTrainer.id,
        };
        this.heldItem = null; // Item instance or null
        if (this.rawPokemonData.genderDistribution.male + this.rawPokemonData.genderDistribution.female < 1) {
            this.gender = '';
        } else {
            this.gender = Math.random() < this.rawPokemonData.genderDistribution.male ? 'Male' : 'Female';
        }

        // Ability selection (stored as base ability; transient override is for battle effects)
        this.abilityOverride = null;
        if (this.rawPokemonData.abilities && this.rawPokemonData.abilities.length > 0) {
            const random = Math.random();
            let cumulative = 0;
            for (const ability of this.rawPokemonData.abilities) {
                cumulative += ability.probability;
                if (random <= cumulative) {
                    this.baseAbility = {
                        identifier: ability.identifier,
                        name: ability.name,
                        description: ability.description || null,
                    };
                    break;
                }
            }
            if (!this.baseAbility) {
                this.baseAbility = {
                    identifier: this.rawPokemonData.abilities[0].identifier,
                    name: this.rawPokemonData.abilities[0].name,
                    description: this.rawPokemonData.abilities[0].description || null,
                };
            }
        } else {
            this.baseAbility = null;
        }

        // Type and Evolution Data
        this.type1 = this.rawPokemonData.type1; // String
        this.type2 = this.rawPokemonData.type2; // String
        this.evolution = this.rawPokemonData.evolution || { level: null, id: null };
        this.evolutionName = pokemonDB[this.evolution.id - 1]?.name ?? null;
        this.canEvolve = false;

        // Stats Setup
        this.battleStages = battleStages; // Object
        this.iv = iv;
        this.ev = ev;
        this.baseStats = this.getBaseStats(this.id);
        this.nature = new Nature();
        this.stats = this.calculateStats(this.id, {
            ev: this.ev,
            iv: this.iv,
            nature: this.nature,
            level: this.level,
        });

        // Experience and EV Setup
        this.experienceType = this.rawPokemonData.experienceType ?? 'medium_fast';
        this.experience = this.constructor.experienceForLevel(experienceType, this.level);
        this.evYield = this.rawPokemonData.evYield;
        this.experienceYield = Math.round((this.evYield.exp * this.level) / 7);

        // Stat History Tracking
        this.statIncreaseHistory = {
            [this.level]: {
                increase: {
                    hp: 0,
                    attack: 0,
                    defense: 0,
                    specialAttack: 0,
                    specialDefense: 0,
                    speed: 0,
                },
                total: {
                    hp: this.stats.maxHp,
                    attack: this.stats.attack,
                    defense: this.stats.defense,
                    specialAttack: this.stats.specialAttack,
                    specialDefense: this.stats.specialDefense,
                    speed: this.stats.speed,
                },
            },
        };

        // Moves Setup
        this.moveSet = globalPokemonDataStore[this.id].moveSetByLevelUp;
        this.moveSetByTM = globalPokemonDataStore[this.id].moveSetByTM;
        this.moveSetByHM = globalPokemonDataStore[this.id].moveSetByHM;
        this.moveSetTMHM = [...this.moveSetByTM, ...this.moveSetByHM];
        if (moves) {
            this.moves = moves;
        } else {
            this.moves = this.getMoves();
        }
        this.moveHistory = Object.values(this.moves);
        this.newMoves = [];

        // Status Data
        this.status1 = null; // Paralisys, Poison, Burn, Freeze, Sleep
        this.status2 = null; // Confusion, Attraction

        // Each value corresponds to the number of turns remaining with the status
        this.volatileStatuses = {
            // Major
            ability_change: 0, // TODO
            ability_suppression: 0, // TODO
            type_change: 0, // TODO
            mimic: 0, // TODO
            substitute: 0, // TODO
            transformed: 0, // TODO
            illusion: 0, // TODO

            // Damaging
            bound: 0, // TODO
            curse: 0, // TODO
            nightmare: 0, // TODO
            perish_song: 0, // TODO
            seeded: 0,
            salt_cure: 0, // TODO

            // Effectiveness
            identified: 0, // TODO
            minimized: 0, // TODO
            tar_shot: 0, // TODO

            // Groundedness
            groudned: 0, // TODO
            magnetic_levitation: 0, // TODO
            telekinesis: 0, // TODO

            // Healing
            aqua_ring: 0, // TODO
            rooted: 0, // TODO

            // Next turn
            laser_focus: 0, // TODO
            taking_aim: 0, // TODO
            drowsy: 0, // TODO

            // Priming
            charged: 0, // TODO
            stockpile: 0, // TODO
            defense_curl: 0, // TODO

            // Prevention
            no_retreat: 0, // TODO
            octolock: 0, // TODO
            disable: 0, // TODO
            embargo: 0, // TODO
            heal_block: 0, // TODO
            imprison: 0, // TODO
            taunt: 0, // TODO
            throat_chop: 0, // TODO
            torment: 0, // TODO
            confusion: 0,
            infatuation: 0, // TODO

            // Stats
            pumped: 0,
            guardSpec: 0,
            guard_split: 0, // TODO
            power_split: 0, // TODO
            speed_swap: 0, // TODO
            power_trick: 0, // TODO

            // Forced Move
            choice_lock: 0, // TODO
            encore: 0, // TODO
            rampage: 0, // TODO
            rolling: 0, // TODO
            uproar: 0, // TODO

            // Multi-turn move
            bide: 0, // TODO
            recharge: 0, // TODO
            charging_turn: 0, // TODO
            semi_invulnerable: 0, // TODO

            // Transient
            flinch: 0,
            bracing: 0, // TODO
            center_of_attention: 0, // TODO
            magic_coat: 0, // TODO
            protect: 0,

            // Forced Out
            forced_out: 0,
        };
        this.inBattleStatusHistory = [];

        // Load sprite sheets and icon
        const frontSpriteSheet = imageStore.get(
            'img/spritesheets/pokemon_front_master_spritesheet_20_20.png'
        );
        const backSpriteSheet = imageStore.get('img/spritesheets/pokemon_back_master_spritesheet_20_20.png');
        const miniSpriteSheet = imageStore.get('img/spritesheets/pokemon_mini_master_spritesheet_20_20.png');
        const statIncreaseImage = imageStore.get('img/stat_boost_animation_v2.png');
        const statDecreaseImage = imageStore.get('img/stat_decrease_animation_v2.png');

        this.frontSprite = spriteStore.store.frontSpriteSheet;
        this.frontSprite.spriteIndex = this.id - 1;
        this.backSprite = spriteStore.store.backSpriteSheet;
        this.backSprite.spriteIndex = this.id - 1;
        this.icon = spriteStore.store.miniSpriteSheet;
        this.icon.spriteIndex = this.id - 1;

        this.statChangeAnimation = new Sprite({
            position: { x: 0, y: 0 },
            frames: { max: 16 },
            hold: 1,
            moving: true,
            visible: true,
            image: imageStore.get('img/attack_stat_animation.png'),
            opacity: 0,
        });

        this.statChangeAnimations = {
            attack: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/attack_stat_animation.png'),
                opacity: 0,
            }),
            defense: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/defense_stat_animation.png'),
                opacity: 0,
            }),
            specialAttack: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/special_attack_stat_animation.png'),
                opacity: 0,
            }),
            specialDefense: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/special_defense_stat_animation.png'),
                opacity: 0,
            }),
            speed: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/speed_stat_animation.png'),
                opacity: 0,
            }),
            accuracy: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/accuracy_stat_animation.png'),
                opacity: 0,
            }),
            evasiveness: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/evasiveness_stat_animation.png'),
                opacity: 0,
            }),
            mix: new Sprite({
                position: { x: 0, y: 0 },
                frames: { max: 16 },
                hold: 1,
                moving: true,
                visible: true,
                image: imageStore.get('img/mix_stat_animation.png'),
                opacity: 0,
            }),
        };

        // Animation sprites (non-spritesheet)
        this.statIncreaseAnimation = new Sprite({
            position: { x: 0, y: 0 },
            frames: { max: 16 },
            hold: 1,
            moving: true,
            visible: true,
            image: imageStore.get('img/attack_stat_animation.png'),
            opacity: 0,
        });
        this.statDecreaseAnimation = this.createSprite({
            position: this.position,
            frames: { max: 64, val: 0 },
            hold: 1,
            moving: true,
            visible: false,
            image: statDecreaseImage,
        });

        this.handleAsyncResource(
            frontSpriteSheet,
            (image) => {
                this.rawPokemonData.frontImage = image;
                this.frontSprite.setImage(image);
            },
            'front sprite sheet'
        );

        this.handleAsyncResource(
            backSpriteSheet,
            (image) => this.backSprite.setImage(image),
            'back sprite sheet'
        );

        this.handleAsyncResource(miniSpriteSheet, (image) => this.icon.setImage(image), 'icon sprite');

        this.handleAsyncResource(
            statIncreaseImage,
            (image) => this.statIncreaseAnimation.setImage(image),
            'stat increase animation'
        );

        this.handleAsyncResource(
            statDecreaseImage,
            (image) => this.statDecreaseAnimation.setImage(image),
            'stat decrease animation'
        );

        // poke, great, ultra, master
        this.pokeball = pokeball ? pokeball : new Pokeball({ id: 'pokeball' });
    }

    loadBattleCry() {
        this.battleCry = audioManager.get(
            `audio/battle_cries/cries/${this.id.toString().padStart(3, '0')}.mp3`
        );
    }

    checkCanEvolveViaLevelUp() {
        if (this.evolution.level === null) {
            return false;
        }
        return this.level >= this.evolution.level;
    }

    evolve() {
        if (this.evolution.id === null) {
            console.error(`${this.name} does not evolve.`);
            return;
        }
        // Get experience above the current experience for level.
        // For example, Mudkip evolves at level 16 and has 2550 EXP,
        // However the starting EXP for level 16 is 2500, so there
        // is 50 EXP left over which we need to hold on to.
        //
        // The reason for this is that the pokemon into which the
        // current pokemon is evolving may have a different EXP curve.
        // So we will recalculate the experience for level with the new
        // EXP curve and then add the left over EXP which we saved
        // Above.
        const currentExpForLevel = this.constructor.experienceForLevel(this.experienceType, this.level);
        const leftoverExp = this.experience - currentExpForLevel;
        this.id = this.evolution.id;
        this.rawPokemonData = Pokemon.getRawPokemonDataV2(this.id);
        this.name = this.rawPokemonData.name;
        this.baseStats = this.getBaseStats(this.id);
        this.stats = this.calculateStats(this.id, {
            ev: this.ev,
            iv: this.iv,
            nature: this.nature,
            level: this.level,
            previousHp: this.stats.hp,
            previousMaxHp: this.stats.maxHp,
        });
        this.type1 = this.rawPokemonData.type1;
        this.type2 = this.rawPokemonData.type2;
        this.evolution = this.rawPokemonData.evolution || { level: null, id: null };

        this.moveSet = globalPokemonDataStore[this.id].moveSetByLevelUp;
        this.moveSetByTM = globalPokemonDataStore[this.id].moveSetByTM;
        this.moveSetByHM = globalPokemonDataStore[this.id].moveSetByHM;
        this.moveSetTMHM = [...this.moveSetByHM, ...this.moveSetByTM];

        this.experienceType = this.rawPokemonData.experienceType ?? 'medium_fast';
        this.experience = this.constructor.experienceForLevel(this.experienceType, this.level) + leftoverExp;
        this.evYield = this.rawPokemonData.evYield;
        this.experienceYield = Math.round((this.evYield.exp * this.level) / 7);

        const battleCry = audioManager.get(
            `audio/battle_cries/cries/${this.id.toString().padStart(3, '0')}.mp3`
        );

        this.battleCry = battleCry;
        this.handleAsyncResource(battleCry, (audio) => (this.battleCry = audio), 'battle cry audio');

        // TODO: Add entry to pokedex
        this.canEvolve = false;
    }

    heal() {
        this.stats.hp = this.stats.maxHp;
        this.status1 = null;
        this.status2 = null;
        if (this.moves[0]) {
            this.moves[0].pp = this.moves[0].maxPP;
        }
        if (this.moves[1]) {
            this.moves[1].pp = this.moves[1].maxPP;
        }
        if (this.moves[2]) {
            this.moves[2].pp = this.moves[2].maxPP;
        }
        if (this.moves[3]) {
            this.moves[3].pp = this.moves[3].maxPP;
        }
    }

    isHealed() {
        if (this.stats.hp < this.stats.maxHp) {
            return false;
        }
        if (this.status1 !== null || this.status2 !== null) {
            return false;
        }
        for (let i = 0; i <= 3; i++) {
            if (this.moves[i] && this.moves[i].pp < this.moves[i].maxPP) {
                return false;
            }
        }
        return true;
    }

    // Get experience
    // SRC: https://bulbapedia.bulbagarden.net/wiki/Experience
    // TODO: Implement "erratic" and "fluctuating"
    static experienceForLevel(experienceType, level) {
        const experienceTypeMap = {
            fast: (4 * level ** 3) / 5,
            medium_fast: level ** 3,
            medium_slow: (6 / 5) * level ** 3 - 15 * level ** 2 + 100 * level - 140,
            slow: (5 * level ** 3) / 4,
        };
        return Math.floor(experienceTypeMap[experienceType]);
    }

    getLevelExperience(level) {
        return Pokemon.experienceForLevel(this.experienceType, level);
    }

    getNextLevelExperience() {
        return Pokemon.experienceForLevel(this.experienceType, this.level + 1);
    }

    getCurrentLevelExperience() {
        return Pokemon.experienceForLevel(this.experienceType, this.level);
    }

    getTotalEVs() {
        return (
            this.ev.hp +
            this.ev.attack +
            this.ev.defense +
            this.ev.specialAttack +
            this.ev.specialDefense +
            this.ev.speed
        );
    }

    refreshStatsFromEVs() {
        this.stats = this.calculateStats(this.id, {
            ev: this.ev,
            iv: this.iv,
            nature: this.nature,
            level: this.level,
            previousHp: this.stats.hp,
            previousMaxHp: this.stats.maxHp,
        });
    }

    updateEVs(foe) {
        const totalEVs =
            this.ev.hp +
            this.ev.attack +
            this.ev.defense +
            this.ev.specialAttack +
            this.ev.specialDefense +
            this.ev.speed;

        if (totalEVs < 510) {
            this.ev.hp += this.ev.hp < 255 ? foe.evYield.hp : 0;
            this.ev.attack += this.ev.attack < 255 ? foe.evYield.attack : 0;
            this.ev.defense += this.ev.defense < 255 ? foe.evYield.defense : 0;
            this.ev.specialAttack += this.ev.specialAttack < 255 ? foe.evYield.specialAttack : 0;
            this.ev.specialDefense += this.ev.specialDefense < 255 ? foe.evYield.specialDefense : 0;
            this.ev.speed += this.ev.speed < 255 ? foe.evYield.speed : 0;
        }
    }

    calculateStatIncrease(nextLevel) {
        // Calculate stats for current level to get baseline (unmodified) stats
        const currentLevelStats = this.calculateStats(this.id, {
            ev: this.ev,
            iv: this.iv,
            nature: this.nature,
            level: this.level,
        });

        // Calculate stats for next level
        const nextLevelStats = this.calculateStats(this.id, {
            ev: this.ev,
            iv: this.iv,
            nature: this.nature,
            level: nextLevel,
        });

        // Calculate the differences between the two levels
        const maxHpIncrease = nextLevelStats.maxHp - currentLevelStats.maxHp;
        const attackIncrease = nextLevelStats.attack - currentLevelStats.attack;
        const defenseIncrease = nextLevelStats.defense - currentLevelStats.defense;
        const specialAttackIncrease = nextLevelStats.specialAttack - currentLevelStats.specialAttack;
        const specialDefenseIncrease = nextLevelStats.specialDefense - currentLevelStats.specialDefense;
        const speedIncrease = nextLevelStats.speed - currentLevelStats.speed;

        const statsIncrease = {
            maxHpIncrease,
            attackIncrease,
            defenseIncrease,
            specialAttackIncrease,
            specialDefenseIncrease,
            speedIncrease,
            nextLevelHP: nextLevelStats.maxHp,
            nextLevelATK: nextLevelStats.attack,
            nextLevelDEF: nextLevelStats.defense,
            nextLevelSPATK: nextLevelStats.specialAttack,
            nextLevelSPDEF: nextLevelStats.specialDefense,
            nextLevelSPE: nextLevelStats.speed,
        };

        return statsIncrease;
    }

    levelUp() {
        if (this.level >= 100) {
            return;
        }

        const statsIncrease = this.calculateStatIncrease(this.level + 1);
        this.level += 1;

        this.stats.maxHp = statsIncrease.nextLevelHP;
        this.stats.hp = this.stats.hp + statsIncrease.maxHpIncrease;
        this.stats.attack = statsIncrease.nextLevelATK;
        this.stats.defense = statsIncrease.nextLevelDEF;
        this.stats.specialAttack = statsIncrease.nextLevelSPATK;
        this.stats.specialDefense = statsIncrease.nextLevelSPDEF;
        this.stats.speed = statsIncrease.nextLevelSPE;

        this.statIncreaseHistory[this.level] = {
            increase: {
                hp: statsIncrease.maxHpIncrease,
                attack: statsIncrease.attackIncrease,
                defense: statsIncrease.defenseIncrease,
                specialAttack: statsIncrease.specialAttackIncrease,
                specialDefense: statsIncrease.specialDefenseIncrease,
                speed: statsIncrease.speedIncrease,
            },
            total: {
                hp: this.stats.maxHp,
                attack: this.stats.attack,
                defense: this.stats.defense,
                specialAttack: this.stats.specialAttack,
                specialDefense: this.stats.specialDefense,
                speed: this.stats.speed,
            },
        };

        if (this.checkCanEvolveViaLevelUp()) {
            this.canEvolve = true;
        }

        return {
            newLevel: this.level,
            statsIncrease: statsIncrease,
        };
    }

    // Soruce: https://bulbapedia.bulbagarden.net/wiki/Experience
    calculateExpGain(defeatedPokemon, numberOfParticipants) {
        let sharedExperienceModifier = 1;
        const expShareInPartyCount = this.currentTrainer.party.map((pokemon) => {
            return pokemon.heldItem.id === 'experience_share' && pokemon.stats.hp > 0;
        }).length;

        if (expShareInPartyCount > 0) {
            // 50% divided equally between all non-fainted exp share hodlers, 50% divided by other non-fainted participants
        } else {
            //
            sharedExperienceModifier = 1 / numberOfParticipants;
        }

        const luckyEggModifier = 1;
        const originalTrainerModifier = 1;
        return (
            defeatedPokemon.experienceYield *
            sharedExperienceModifier *
            luckyEggModifier *
            originalTrainerModifier
        );
    }

    resetBattleStages() {
        this.battleStages = {
            attack: 0,
            defense: 0,
            specialAttack: 0,
            specialDefense: 0,
            speed: 0,
            accuracy: 0,
            evasiveness: 0,
        };
    }

    canApplyItem(item) {
        if (Item.isRareCandy(item.id)) {
            return this.level < 100;
        }

        if (Item.isTMorHM(item.id)) {
            return this.canLearnMove(item.move);
        }
        if (Item.isPokeball(item.id)) {
            return false;
        }
        const movesHaveFullPP =
            (this.moves[0] === null || this.moves[0]?.pp === this.moves[0]?.maxPP) &&
            (this.moves[1] === null || this.moves[1]?.pp === this.moves[1]?.maxPP) &&
            (this.moves[2] === null || this.moves[2]?.pp === this.moves[2]?.maxPP) &&
            (this.moves[3] === null || this.moves[3]?.pp === this.moves[3]?.maxPP);
        if (Item.isMoveItem(item.id)) {
            // Moves have full PP. We cant apply this item.
            return !movesHaveFullPP;
        }
        if (item.id === 'elixir' || item.id === 'max_elixir') {
            return !movesHaveFullPP;
        }
        if (item.id === 'revive' || item.id === 'max_revive') {
            return this.stats.hp === 0;
        }
        if (item.healAmount > 0) {
            return this.stats.hp < this.stats.maxHp && this.stats.hp > 0;
        }
        if (item.healStatus?.length > 0) {
            const hasCurableStatus = !!this.status1 && item.healStatus.includes(this.status1.id);
            const hasConfusion = item.healStatus.includes('confusion') && this.isConfused();
            return hasCurableStatus || hasConfusion;
        }
        if (item.id && pinchBerryStat[item.id]) {
            return this.battleStages[pinchBerryStat[item.id]] < 6;
        }
        if (evVitaminItemIds.includes(item.id)) {
            const stat = evVitaminStat[item.id];
            return (
                stat !== null && stat !== undefined && this.ev[stat] < EV_VITAMIN_STAT_CAP && this.getTotalEVs() + 10 <= EV_TOTAL_MAX
            );
        }
        if (evReducingBerryItemIds.includes(item.id)) {
            const stat = evReducingBerryStat[item.id];
            return stat !== null && stat !== undefined && this.ev[stat] > 0;
        }
        if (['pp_up', 'pp_max'].includes(item.id)) {
            return [this.moves[0], this.moves[1], this.moves[2], this.moves[3]].some(
                (m) => m && m.canApplyItem && m.canApplyItem(item)
            );
        }
        if (xItemIds.includes(item.id)) {
            return this.battleStages[xItemStat[item.id]] < 6;
        }
        if (item.id === 'guard_spec') {
            return !this.volatileStatuses.guardSpec;
        }
        if (item.id === 'dire_hit') {
            return this.volatileStatuses.pumped === 0;
        }
    }

    applyItem(item) {
        let fullyApplied = false;
        let applicationText = '';
        if (!this.canApplyItem(item)) {
            return {
                applicationComplete: true,
                applicationText: "It won't have any effect...",
            };
        }

        if (item.isTMorHM()) {
            console.log('HM can be applied!');
        }

        let result;

        switch (item.id) {
            case 'rare_candy':
                if (this.level < 100) {
                    this.experience = this.getNextLevelExperience();
                    this.levelUp();
                    result = {
                        applicationComplete: true,
                        applicationText: `${this.name} grew to level ${this.level + 1}`,
                    };
                }
                break;
            case 'max_revive':
                result = {
                    applicationComplete: true,
                    applicationText: '',
                };
                break;
            case 'revive':
                result = {
                    applicationComplete: true,
                    applicationText: '',
                };
                break;
            case 'elixir':
                [this.moves[0], this.moves[1], this.moves[2], this.moves[3]].forEach((move) => {
                    if (move !== null || move !== undefined) {
                        const addedPP = Math.max((10, move.maxPP - move.pp));
                        move.pp += addedPP;
                    }
                });
                this.currentTrainer.useItemV2(item.id);
                result = {
                    applicationComplete: true,
                    applicationText: 'PP for all moves was restored!',
                };
                break;
            case 'max_elixir':
                [this.moves[0], this.moves[1], this.moves[2], this.moves[3]].forEach((move) => {
                    if (move !== null || move !== undefined) {
                        move.pp = move.maxPP;
                    }
                });
                this.currentTrainer.useItemV2(item.id);
                result = {
                    applicationComplete: true,
                    applicationText: 'PP for all moves was fully restored!',
                };
                break;

            case 'super_potion':
            case 'hyper_potion':
            case 'max_potion':
            case 'full_restore':
            case 'antidote':
            case 'burn_heal':
            case 'ice_heal':
            case 'awakening':
            case 'paralyze_heal':
            case 'potion':
            case 'oran_berry':
            case 'sitrus_berry':
            case 'berry_juice':
                if (item.healStatus?.includes(this.status1?.id)) {
                    this.status1 = null;
                }
                item.healAmount -= 1;
                this.stats.hp += 1;
                applicationText = `${this.name}'s health was restored!`;
                fullyApplied = this.stats.hp === this.stats.maxHp || item.healAmount === 0;
                if (fullyApplied) {
                    this.currentTrainer.useItemV2(item.id);
                }
                result = {
                    applicationComplete: fullyApplied,
                    applicationText: applicationText,
                };
                break;
            case 'cheri_berry':
            case 'chesto_berry':
            case 'pecha_berry':
            case 'rawst_berry':
            case 'aspear_berry':
            case 'persim_berry':
            case 'lum_berry':
                if (item.healStatus?.includes(this.status1?.id)) {
                    applicationText = `${this.name} was cured of its ${this.status1.id}!`;
                    this.status1 = null;
                }
                if (item.healStatus?.includes('confusion') && this.isConfused()) {
                    this.volatileStatuses.confusion = 0;
                    applicationText = applicationText
                        ? `${applicationText} Its confusion was cleared!`
                        : `${this.name} snapped out of confusion!`;
                }
                this.currentTrainer.useItemV2(item.id);
                result = {
                    applicationComplete: true,
                    applicationText: applicationText || `${this.name} was cured!`,
                };
                break;
            case 'liechi_berry':
            case 'ganlon_berry':
            case 'petaya_berry':
            case 'apicot_berry':
            case 'salac_berry':
                if (pinchBerryStat[item.id]) {
                    const stat = pinchBerryStat[item.id];
                    const statNames = {
                        attack: 'Attack',
                        defense: 'Defense',
                        specialAttack: 'Special Attack',
                        specialDefense: 'Special Defense',
                        speed: 'Speed',
                    };
                    this.battleStages[stat] = Math.min(6, this.battleStages[stat] + 1);
                    this.currentTrainer.useItemV2(item.id);
                    result = {
                        applicationComplete: true,
                        applicationText: `${this.name}'s ${statNames[stat]} rose!`,
                    };
                }
                break;
            case 'hp_up':
            case 'protein':
            case 'iron':
            case 'calcium':
            case 'zinc':
            case 'carbos':
                {
                    const stat = evVitaminStat[item.id];
                    if (stat !== null && stat !== undefined) {
                        this.ev[stat] += 10;
                        this.refreshStatsFromEVs();
                        const statNames = {
                            hp: 'HP',
                            attack: 'Attack',
                            defense: 'Defense',
                            specialAttack: 'Special Attack',
                            specialDefense: 'Special Defense',
                            speed: 'Speed',
                        };
                        result = {
                            applicationComplete: true,
                            applicationText: `${this.name}'s ${statNames[stat]} base points rose!`,
                        };
                    }
                }
                break;
            case 'pomeg_berry':
            case 'kelpsy_berry':
            case 'qualot_berry':
            case 'hondew_berry':
            case 'grepa_berry':
            case 'tamato_berry':
                {
                    const stat = evReducingBerryStat[item.id];
                    if (stat !== null && stat !== undefined) {
                        this.ev[stat] = Math.max(0, this.ev[stat] - 10);
                        this.refreshStatsFromEVs();
                        const statNames = {
                            hp: 'HP',
                            attack: 'Attack',
                            defense: 'Defense',
                            specialAttack: 'Special Attack',
                            specialDefense: 'Special Defense',
                            speed: 'Speed',
                        };
                        result = {
                            applicationComplete: true,
                            applicationText: `${this.name}'s ${statNames[stat]} base points fell!`,
                        };
                    }
                }
                break;
            case 'x_attack':
            case 'x_defend':
            case 'x_speed':
            case 'x_special_attack':
            case 'x_special_defense':
            case 'x_accuracy': {
                const stat = xItemStat[item.id];
                const statNames = {
                    attack: 'Attack',
                    defense: 'Defense',
                    specialAttack: 'Sp. Atk',
                    specialDefense: 'Sp. Def',
                    speed: 'Speed',
                    accuracy: 'Accuracy',
                };
                this.battleStages[stat] = Math.min(6, this.battleStages[stat] + 2);
                result = {
                    applicationComplete: true,
                    applicationText: `${this.name}'s ${statNames[stat]} rose sharply!`,
                };
                break;
            }
            case 'guard_spec':
                this.volatileStatuses.guardSpec = 5;
                result = {
                    applicationComplete: true,
                    applicationText: `${this.name} is protected against stat changes!`,
                };
                break;
            case 'dire_hit':
                this.volatileStatuses.pumped = 2;
                result = {
                    applicationComplete: true,
                    applicationText: `${this.name} is getting pumped!`,
                };
                break;
        }

        return result;
    }

    resetBackSprite() {
        this.backSprite.position = {
            x: 32,
            y: 48,
        };
        this.backSprite.scale = 0;
        this.backSprite.verticalCrop = 0;
        this.backSprite.verticalOffset = 0;
    }

    catchRate(ballUsed) {
        let ballBonus = 1;
        if (ballUsed.id === 'master_ball') {
            ballBonus = 255;
        } else if (ballUsed.id === 'ultra_ball') {
            ballBonus = 2;
        } else if (ballUsed.id === 'great_ball') {
            ballBonus = 1.5;
        } else if (ballUsed.id === 'pokeball') {
            ballBonus = 1;
        } else {
            ballBonus = 1;
        }
        const catchRate = catchRateDB[String(this.id)]['catch_rate'];
        return Math.floor(
            ((3 * this.stats.maxHp - 2 * this.stats.hp) * catchRate * ballBonus * this.catchBonus()) /
                (3 * this.stats.maxHp)
        );
    }

    catchBonus() {
        if (this.status1 === null) {
            return 1;
        }
        if (
            this.status1 === 'paralysis' ||
            this.status1 === 'poison' ||
            this.status1 === 'burn' ||
            this.status1 === 'bad_poison'
        ) {
            return 1.5;
        }
        if (this.status1.id === 'sleep' || this.status1.id === 'freeze') {
            return 2;
        }
        return 1;
    }

    isWild() {
        return this.originalTrainer.id === null;
    }

    getFirstEmptyMoveSlot() {
        if (!this.moves[0]) {
            return 0;
        }
        if (!this.moves[1]) {
            return 1;
        }
        if (!this.moves[2]) {
            return 2;
        }
        if (!this.moves[3]) {
            return 3;
        }
        return undefined;
    }

    hasEmptyMoveSlot() {
        return !this.moves[0] || !this.moves[1] || !this.moves[2] || !this.moves[3];
    }

    hasNewMoveAvailable() {
        if (!this.moveSet[this.level]) {
            return false;
        }
        return this.moveSet[this.level].length > 0;
    }

    getNextLearnableMoveForCurrentLevel() {
        return Move.getById(this.moveSet[this.level]);
    }

    canLearnMove(move) {
        if (this.knowsMove(move)) {
            return false;
        }
        if (this.moveSetTMHM) {
            return this.moveSetTMHM.includes(move.name.toLowerCase().replace(' ', '_'));
        } else {
            console.warn('TM and HM list not defined for this pokemon.');
            return false;
        }
    }

    moveLearnabilityText(move) {
        if (this.knowsMove(move)) {
            return 'LEARNED';
        }
        if (this.canLearnMove(move)) {
            return 'ABLE';
        }
        return 'UNABLE';
    }

    knowsMove(move) {
        return [this.moves[0]?.name, this.moves[1]?.name, this.moves[2]?.name, this.moves[3]?.name].includes(
            move.name
        );
    }

    learnMove(move) {
        if (this.hasEmptyMoveSlot()) {
            const slot = this.getFirstEmptyMoveSlot();
            this.moves[slot] = move;
            this.moveHistory.push(move);
        } else {
            console.warn('Unable to learn move. No move slots were available.');
        }
    }

    getMostRecentLearnedMove() {
        return this.moveHistory[this.moveHistory.length - 1];
    }

    addAnimation(animation) {
        return this.currentSprite.animationQueue.push(animation);
    }

    getAnimationQueue() {
        return this.currentSprite.animationQueue;
    }

    shiftAnimation() {
        return this.currentSprite.animationQueue.shift();
    }

    isParalyzed() {
        return this.status1?.id === 'paralysis';
    }

    isFlinched() {
        return this.volatileStatuses.flinch > 0;
    }

    isBurned() {
        return this.status1?.id === 'burn';
    }

    isPinched() {
        return this.stats.hp < Math.round(this.stats.maxHp * 0.5);
    }

    isStatused() {
        return this.status1 !== null;
    }

    isConfused() {
        return this.volatileStatuses.confusion > 0;
    }

    isRooted() {
        return this.volatileStatuses.rooted > 0;
    }

    isSeeded() {
        return this.volatileStatuses.seeded > 0;
    }

    isAquaRinged() {
        return this.volatileStatuses.aqua_ring > 0;
    }

    /**
     * Calculate damage from an entry hazard.
     * @param {HazardType} hazardType - The type of entry hazard
     * @param {number} layers - The number of layers of this hazard
     * @returns {number} The damage amount
     */
    calculateEntryHazardDamage(hazardType, layers) {
        return calculateEntryHazardDamage(hazardType, layers, this);
    }

    /**
     * Get the status effect applied by an entry hazard.
     * @param {HazardType} hazardType - The type of entry hazard
     * @param {number} layers - The number of layers of this hazard
     * @returns {string|null} The status effect to apply, or null if none
     */
    getEntryHazardStatusEffect(hazardType, layers) {
        return calculateEntryHazardStatusEffect(hazardType, layers);
    }

    /**
     * Get stat modifications from an entry hazard.
     * @param {HazardType} hazardType - The type of entry hazard
     * @returns {{stat: string, stages: number}|null} Object with stat modifications, or null if none
     */
    getEntryHazardStatModification(hazardType) {
        return calculateEntryHazardStatModification(hazardType);
    }

    updateVolatileStatusTurns() {
        // Happens at the end of the battle turn
        const permanentStatuses = new Set(['pumped']);
        const volatileStatusesSnapshot = {};
        Object.keys(this.volatileStatuses).forEach((key) => {
            volatileStatusesSnapshot[key] = this.volatileStatuses[key];
            if (!permanentStatuses.has(key)) {
                this.volatileStatuses[key] = Math.max(0, this.volatileStatuses[key] - 1);
            }
        });
        this.inBattleStatusHistory.unshift(volatileStatusesSnapshot);
    }

    resetVolatileStatuses() {
        this.clearAbilityOverride();
        this.volatileStatuses = {
            // Major
            ability_change: 0, // TODO
            ability_suppression: 0, // TODO
            type_change: 0, // TODO
            mimic: 0, // TODO
            substitute: 0, // TODO
            transformed: 0, // TODO
            illusion: 0, // TODO

            // Damaging
            bound: 0, // TODO
            curse: 0, // TODO
            nightmare: 0, // TODO
            perish_song: 0, // TODO
            seeded: 0, // TODO
            salt_cure: 0, // TODO

            // Effectiveness
            identified: 0, // TODO
            minimized: 0, // TODO
            tar_shot: 0, // TODO

            // Groundedness
            groudned: 0, // TODO
            magnetic_levitation: 0, // TODO
            telekinesis: 0, // TODO

            // Healing
            aqua_ring: 0, // TODO
            rooted: 0, // TODO

            // Next turn
            laser_focus: 0, // TODO
            taking_aim: 0, // TODO
            drowsy: 0, // TODO

            // Priming
            charged: 0, // TODO
            stockpile: 0, // TODO
            defense_curl: 0, // TODO

            // Prevention
            no_retreat: 0, // TODO
            octolock: 0, // TODO
            disable: 0, // TODO
            embargo: 0, // TODO
            heal_block: 0, // TODO
            imprison: 0, // TODO
            taunt: 0, // TODO
            throat_chop: 0, // TODO
            torment: 0, // TODO
            confusion: 0,
            infatuation: 0, // TODO

            // Stats
            pumped: 0,
            guardSpec: 0,
            guard_split: 0, // TODO
            power_split: 0, // TODO
            speed_swap: 0, // TODO
            power_trick: 0, // TODO

            // Forced Move
            choice_lock: 0, // TODO
            encore: 0, // TODO
            rampage: 0, // TODO
            rolling: 0, // TODO
            uproar: 0, // TODO

            // Multi-turn move
            bide: 0, // TODO
            recharge: 0, // TODO
            charging_turn: 0, // TODO
            semi_invulnerable: 0, // TODO

            // Transient
            flinch: 0,
            bracing: 0, // TODO
            center_of_attention: 0, // TODO
            magic_coat: 0, // TODO
            protect: 0,

            // Forced Out
            forced_out: 0,
        };
    }

    getVolatileStatuses() {
        return Object.keys(this.volatileStatuses).map((vs) => vs);
    }

    shouldSnapOutOfConfusion() {
        if (this.inBattleStatusHistory.length < 2) {
            // Not possible to have been confused in the first place, not enough time has passed.
            return false;
        }

        // If the previous turn had 1 confusion turn remaining and the current turn
        return this.inBattleStatusHistory[0].confusion === 1 && this.volatileStatuses.confusion === 0;
    }

    getCriticalHitStage() {
        return Math.min(this.volatileStatuses.pumped, 4);
    }

    getDamageDealtByPrimaryStatus() {
        if (this.status1) {
            return this.status1.getEffectDamage() * -1;
        }
        return 0;
    }

    getDamageDealtByVolatileStatus(status) {
        switch (status) {
            case 'seeded':
                return Math.ceil(this.stats.maxHp * (1 / 16)) * -1;
        }
    }

    getHPRecoveredByVolatileStatus(status) {
        switch (status) {
            case 'aqua_ring':
            case 'rooted':
                return Math.ceil(this.stats.maxHp * (1 / 16));
        }
    }

    isHPFull() {
        return this.stats.hp === this.stats.maxHp;
    }

    isProtected() {
        return this.volatileStatuses.protect > 0;
    }

    isForcedOut() {
        return this.volatileStatuses.forced_out > 0;
    }

    isFainted() {
        return this.stats.hp <= 0;
    }

    identifyBy(pokemon) {
        this.identifiedBy = pokemon;
    }

    getIdentifiedBy() {
        return this.identifiedBy;
    }

    isBackFacing() {
        return this.backSprite.image.src === this.currentSprite?.image?.src;
    }

    isFrontFacing() {
        return this.frontSprite.image.src === this.currentSprite?.image?.src;
    }

    static createIconSpriteForId(id) {
        return new Sprite({
            image: imageStore.get('img/spritesheets/pokemon_mini_master_spritesheet_20_20.png'),
            spriteIndex: id - 1,
            isFromSpriteSheet: true,
            spriteSheetCols: 20,
            spriteSheetRows: 20,
            visible: true,
            position: {
                x: 0,
                y: 0,
            },
        });
    }
}
